import React, { useEffect, useMemo, useRef, useState } from 'react';
import api from '../api/api';
import { formatDateTime } from '../utils/dashboardTranslations';

function VendorDriverRequests() {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [markingIds, setMarkingIds] = useState({});
    const intervalRef = useRef(null);

    const unreadCount = useMemo(
        () => requests.filter((request) => !request.isRead).length,
        [requests]
    );

    const fetchRequests = async (isBackground = false) => {
        if (!isBackground) {
            setLoading(true);
        }

        try {
            const response = await api.get('/notifications/admin/vendor-driver-requests', {
                params: { limit: 100 }
            });
            setRequests(Array.isArray(response.data) ? response.data : []);
        } catch (error) {
            console.error('Error fetching vendor driver requests:', error);
            if (!isBackground) {
                alert('فشل تحميل طلبات السائقين');
            }
        } finally {
            if (!isBackground) {
                setLoading(false);
            }
        }
    };

    useEffect(() => {
        fetchRequests();

        intervalRef.current = setInterval(() => {
            fetchRequests(true);
        }, 10000);

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, []);

    const markAsReviewed = async (requestId) => {
        setMarkingIds((previous) => ({ ...previous, [requestId]: true }));

        try {
            await api.post(`/notifications/${requestId}/read`);
            setRequests((previous) =>
                previous.map((request) =>
                    request.id === requestId
                        ? { ...request, isRead: true }
                        : request
                )
            );
        } catch (error) {
            console.error('Error marking vendor driver request as reviewed:', error);
            alert('فشل تحديث حالة الطلب');
        } finally {
            setMarkingIds((previous) => ({ ...previous, [requestId]: false }));
        }
    };

    return (
        <div className="container-fluid" dir="rtl">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-4">
                <div>
                    <h2 className="mb-1">طلبات السائقين من المتاجر</h2>
                    <p className="text-muted mb-0">
                        الطلبات المرسلة من شاشة التاجر للطلبات الخارجية أو الهاتفية.
                    </p>
                </div>

                <button
                    className="btn btn-outline-primary"
                    onClick={() => fetchRequests()}
                    disabled={loading}
                >
                    <i className="fas fa-rotate-left me-2"></i>
                    تحديث
                </button>
            </div>

            <div className="row g-3 mb-4">
                <div className="col-md-6">
                    <div className="card border-0 shadow-sm">
                        <div className="card-body">
                            <div className="text-muted mb-2">إجمالي الطلبات</div>
                            <div style={{ fontSize: '2rem', fontWeight: 800 }}>{requests.length}</div>
                        </div>
                    </div>
                </div>
                <div className="col-md-6">
                    <div className="card border-0 shadow-sm">
                        <div className="card-body">
                            <div className="text-muted mb-2">الطلبات غير المقروءة</div>
                            <div style={{ fontSize: '2rem', fontWeight: 800, color: '#dc3545' }}>
                                {unreadCount}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="card border-0 shadow-sm">
                <div className="card-body">
                    {loading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border" role="status">
                                <span className="visually-hidden">جاري التحميل...</span>
                            </div>
                        </div>
                    ) : requests.length === 0 ? (
                        <div className="alert alert-info mb-0">
                            لا توجد طلبات سائقين من المتاجر حالياً.
                        </div>
                    ) : (
                        <div className="row g-3">
                            {requests.map((request) => {
                                const locationUrl = request.vendorLocation
                                    ? `https://maps.google.com/maps?q=${encodeURIComponent(request.vendorLocation)}`
                                    : null;

                                return (
                                    <div className="col-12" key={request.id}>
                                        <div
                                            className="border rounded-3 p-3"
                                            style={{
                                                backgroundColor: request.isRead ? '#ffffff' : '#fff8e8',
                                                borderColor: request.isRead ? '#dee2e6' : '#f0ad4e'
                                            }}
                                        >
                                            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-3">
                                                <div>
                                                    <div className="d-flex align-items-center gap-2 flex-wrap mb-2">
                                                        <h5 className="mb-0">{request.vendorName || 'متجر غير محدد'}</h5>
                                                        <span className={`badge ${request.isRead ? 'bg-secondary' : 'bg-warning text-dark'}`}>
                                                            {request.isRead ? 'تمت المراجعة' : 'جديد'}
                                                        </span>
                                                    </div>
                                                    <div className="text-muted small">
                                                        {formatDateTime(request.createdAt)}
                                                    </div>
                                                </div>
                                                <div className="text-muted small">
                                                    رقم الطلب الداخلي: #{request.id}
                                                </div>
                                            </div>

                                            <p className="mb-3">{request.body}</p>

                                            <div className="row g-2 mb-3">
                                                <div className="col-md-4">
                                                    <div className="small text-muted mb-1">رقم المتجر</div>
                                                    <div>{request.vendorId || '—'}</div>
                                                </div>
                                                <div className="col-md-4">
                                                    <div className="small text-muted mb-1">رقم الهاتف</div>
                                                    <div>{request.vendorPhoneNumber || 'غير متوفر'}</div>
                                                </div>
                                                <div className="col-md-4">
                                                    <div className="small text-muted mb-1">الموقع</div>
                                                    <div>{request.vendorLocation || 'غير متوفر'}</div>
                                                </div>
                                            </div>

                                            <div className="d-flex gap-2 flex-wrap">
                                                {request.vendorPhoneNumber && (
                                                    <a
                                                        className="btn btn-success btn-sm"
                                                        href={`tel:${request.vendorPhoneNumber}`}
                                                    >
                                                        <i className="fas fa-phone me-1"></i>
                                                        اتصال بالمتجر
                                                    </a>
                                                )}

                                                {locationUrl && (
                                                    <a
                                                        className="btn btn-outline-primary btn-sm"
                                                        href={locationUrl}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                    >
                                                        <i className="fas fa-location-dot me-1"></i>
                                                        فتح الموقع
                                                    </a>
                                                )}

                                                <button
                                                    className="btn btn-outline-dark btn-sm"
                                                    onClick={() => markAsReviewed(request.id)}
                                                    disabled={request.isRead || markingIds[request.id]}
                                                >
                                                    {markingIds[request.id] ? (
                                                        <>
                                                            <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                                                            جاري التحديث...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <i className="fas fa-check me-1"></i>
                                                            تمّت المراجعة
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default VendorDriverRequests;
